import type { ComputedRef } from 'vue';
import type { FormatLocale, UiLanguage } from '#layers/base/types/locale';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getBrowserLocale, resolveFormatLocale, toUiLanguage } from '#layers/base/utils/locale';

interface UseUserLocaleReturn {
	/** Selects the vue-i18n translation catalog. */
	language: ComputedRef<UiLanguage>;
	/** Handed to every `Intl` formatter. */
	formatLocale: ComputedRef<FormatLocale>;
}

/**
 * Single source of truth for the two locales Studio runs on: the language the UI is written
 * in, and the tag handed to `Intl` and `toLocaleString`.
 *
 * This is the app-owned source. Nuxt UI's `useLocale().code` is downstream of it — `useNuxtUiLocale`
 * feeds `formatLocale` into that object so reka formats with it — so reading `code` back tells you
 * nothing `formatLocale` doesn't, and its bundled catalog falls back to English for anything unmapped.
 *
 * The language comes from the active i18n locale; the format locale pairs it with the region the
 * browser reports, the same source the datetime blocks already use for the timezone. When an
 * explicit region setting lands it replaces the browser lookup here, and no call site moves.
 */
export function useUserLocale(): UseUserLocaleReturn {
	const { locale } = useI18n();
	const language = computed(() => toUiLanguage(locale.value));

	return {
		language,
		formatLocale: computed(() => resolveFormatLocale(language.value, getBrowserLocale())),
	};
}
